import { z } from "zod";

// ── Complexity ──────────────────────────────────────────────

export const Complexity = z.enum(["S", "M", "L", "XL"]);
export type Complexity = z.infer<typeof Complexity>;

export const COMPLEXITY_MULTIPLIERS: Record<Complexity, number> = {
  S: 0.3,
  M: 1.0,
  L: 2.5,
  XL: 5.0,
};

// ── Task Types ──────────────────────────────────────────────

export const TaskType = z.enum([
  "scaffold",
  "api-endpoint",
  "react-component",
  "complex-ui",
  "database-schema",
  "integration",
  "test-unit",
  "test-e2e",
  "devops",
  "documentation",
  "refactoring",
  "architecture",
  "debugging",
]);
export type TaskType = z.infer<typeof TaskType>;

// ── Task Profile ────────────────────────────────────────────

export const TaskProfile = z.object({
  input: z.number(),
  output: z.number(),
  total: z.number(),
  notes: z.string().optional(),
});
export type TaskProfile = z.infer<typeof TaskProfile>;

// ── Model Pricing ───────────────────────────────────────────

export const ModelTier = z.enum(["frontier", "balanced", "speed"]);
export type ModelTier = z.infer<typeof ModelTier>;

export const ModelPricing = z.object({
  id: z.string(),
  provider: z.string(),
  name: z.string(),
  input_per_mtok: z.number(),
  output_per_mtok: z.number(),
  cache_read_per_mtok: z.number(),
  batch_discount: z.number(),
  tier: ModelTier,
});
export type ModelPricing = z.infer<typeof ModelPricing>;

// ── Overhead Config ─────────────────────────────────────────

export const OverheadConfig = z.object({
  multi_turn_context_resend: z.number(),
  tool_call_overhead_input: z.number(),
  tool_call_overhead_output: z.number(),
  extended_thinking_opus: z.number(),
  extended_thinking_sonnet: z.number(),
  multi_agent: z.number(),
  error_recovery_chance: z.number(),
  error_recovery_multiplier: z.number(),
  planning_pass: z.number(),
  review_pass: z.number(),
  context_rot_above_50k: z.number(),
  claude_md_context_load_min: z.number(),
  claude_md_context_load_max: z.number(),
  cache_discount: z.number(),
});
export type OverheadConfig = z.infer<typeof OverheadConfig>;

// ── Agent Recommendation ────────────────────────────────────

export const AgentRecommendation = z.object({
  model: z.string(),
  reason: z.string(),
});
export type AgentRecommendation = z.infer<typeof AgentRecommendation>;

// ── Feature (parsed from spec) ──────────────────────────────

export const Priority = z.enum(["P0", "P1", "P2", "P3"]);
export type Priority = z.infer<typeof Priority>;

export const Feature = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  priority: Priority.optional(),
  complexity: Complexity,
  description: z.string(),
  dependencies: z.array(z.string()).default([]),
  aiTasks: z.array(z.string()).default([]),
  acceptanceCriteria: z.string().optional(),
});
export type Feature = z.infer<typeof Feature>;

// ── Spec Metadata (frontmatter) ─────────────────────────────

export const SpecMetadata = z.object({
  project: z.string().optional(),
  version: z.string().optional(),
  date: z.string().optional(),
  team_size: z.number().optional(),
  client: z.string().optional(),
});
export type SpecMetadata = z.infer<typeof SpecMetadata>;

// ── Parsed Spec ─────────────────────────────────────────────

export const ParsedSpec = z.object({
  metadata: SpecMetadata,
  features: z.array(Feature),
  rawContent: z.string(),
});
export type ParsedSpec = z.infer<typeof ParsedSpec>;

// ── Feature Estimate ────────────────────────────────────────

export const FeatureEstimate = z.object({
  featureId: z.string(),
  featureName: z.string(),
  category: z.string(),
  complexity: Complexity,
  taskTypes: z.array(TaskType),
  recommendedModel: z.string(),
  baseTokens: z.object({ input: z.number(), output: z.number(), total: z.number() }),
  withOverhead: z.object({ input: z.number(), output: z.number(), total: z.number() }),
  estimatedCost: z.number(),
  overheadBreakdown: z.object({
    contextResend: z.number(),
    toolCalls: z.number(),
    thinking: z.number(),
    errorRecovery: z.number(),
    planning: z.number(),
    review: z.number(),
  }),
  confidence: z
    .object({
      low: z.object({ tokens: z.number(), cost: z.number() }),
      expected: z.object({ tokens: z.number(), cost: z.number() }),
      high: z.object({ tokens: z.number(), cost: z.number() }),
    })
    .optional(),
});
export type FeatureEstimate = z.infer<typeof FeatureEstimate>;

// ── Sensitivity Scenario ────────────────────────────────────

export const SensitivityScenario = z.object({
  name: z.string(),
  adjustment: z.number(),
  totalTokens: z.number(),
  totalCost: z.number(),
});
export type SensitivityScenario = z.infer<typeof SensitivityScenario>;

// ── Estimate Report ─────────────────────────────────────────

export const EstimateReport = z.object({
  project: z.string(),
  specHash: z.string(),
  features: z.array(FeatureEstimate),
  totals: z.object({
    tokens: z.number(),
    cost: z.number(),
  }),
  sensitivity: z.array(SensitivityScenario),
  generatedAt: z.string(),
});
export type EstimateReport = z.infer<typeof EstimateReport>;

// ── Feature Registry Entry ──────────────────────────────────

export const RegistryEntry = z.object({
  id: z.string(),
  name: z.string(),
  keywords: z.array(z.string()),
  file_patterns: z.array(z.string()),
  git_branch_patterns: z.array(z.string()),
  estimated_tokens: z.number(),
  status: z.enum(["not-started", "in-progress", "complete"]),
});
export type RegistryEntry = z.infer<typeof RegistryEntry>;

export const FeatureRegistry = z.object({
  project: z.string(),
  features: z.array(RegistryEntry),
  generatedAt: z.string(),
});
export type FeatureRegistry = z.infer<typeof FeatureRegistry>;
